import { useEffect, useState } from 'react'
import NewWedingModal from './NewWedingModal'
import styles from './NewWedingModal.module.scss'
import ButtonCustom from '../../../components/atoms/button/ButtonCustom'
import { toastHelper } from '../../../util/toastHelper'
import { CircularProgress, Checkbox } from '@mui/material'
import Stepper from '../../../components/atoms/Stepper/Stepper'

const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v2'

function TaskListModal({
	open,
	handleClose,
	handleNext,
	weddingType,
	handleBack,
}) {
	const [tasks, setTasks] = useState([])
	const [selectedTasks, setSelectedTasks] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		const fetchTaskList = async () => {
			if (!open || !weddingType) return

			setLoading(true)
			setError(null)

			try {
				const res = await fetch(
					`${REQUEST_URL}/${API_VERSION}/task/task-list-by-type?weddingType=${encodeURIComponent(weddingType)}`,
					{
						method: 'GET',
						credentials: 'include',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
					}
				)

				const result = await res.json()

				if (result.success && result.data) {
					setTasks(result.data)
				} else {
					setError(result.message || 'Failed to fetch task list')
					toastHelper(result.message || 'Failed to fetch task list', 'error')
				}
			} catch (err) {
				console.error('Error fetching task list:', err)
				setError('Failed to fetch task list')
				toastHelper('Failed to fetch task list', 'error')
			} finally {
				setLoading(false)
			}
		}

		fetchTaskList()
	}, [open, weddingType])

	const handleContinue = () => {
		handleNext()
	}

	const toggleTask = (taskId) => {
		setSelectedTasks((prev) =>
			prev.includes(taskId)
				? prev.filter((id) => id !== taskId)
				: [...prev, taskId]
		)
	}

	return (
		<NewWedingModal
			open={open}
			handleClose={handleClose}
			handleBack={handleBack}
			modalImage="/images/GuestCount.jpg"
		>
			<div
				className={`${styles.welcomeModal} ${styles.guestCount} ${styles.modalMainContainer}`}
			>
				<div className={styles.welcomeModalBody}>
					<h2>Select Your Wedding Tasks</h2>
					<p style={{ marginTop: '0.5rem', marginBottom: '0.5rem', color: '#999', fontSize: '0.95rem' }}>
						Choose the tasks you want to include in your planning journey
					</p>
					{selectedTasks.length > 0 && (
						<p style={{ marginBottom: '1rem', color: '#7ca6bd', fontSize: '0.9rem' }}>
							Great! You've selected {selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'}
						</p>
					)}

					{loading ? (
						<div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
							<CircularProgress />
						</div>
					) : error ? (
						<div style={{ padding: '2rem', textAlign: 'center', color: '#d32f2f' }}>
							{error}
						</div>
					) : (
						<div
							style={{ 
								width: '100%', 
								marginTop: '20px', 
								maxHeight: '350px', 
								overflowY: 'auto',
								paddingRight: '8px'
							}}
						>
							{tasks.length > 0 ? (
								<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									{tasks.map((task, idx) => {
										const taskId = task._id || task.id || `${task.category}-${task.task}-${idx}`
										const isSelected = selectedTasks.includes(taskId)
										
										return (
											<div
												key={taskId}
												onClick={() => toggleTask(taskId)}
												style={{
													padding: '16px',
													backgroundColor: isSelected ? '#e8f4f8' : '#fff',
													border: isSelected ? '2px solid #7ca6bd' : '2px solid #e0e0e0',
													borderRadius: '12px',
													cursor: 'pointer',
													transition: 'all 0.2s ease',
													display: 'flex',
													alignItems: 'flex-start',
													gap: '12px',
												}}
											>
												<Checkbox
													checked={isSelected}
													onChange={() => toggleTask(taskId)}
													sx={{
														color: '#7ca6bd',
														padding: 0,
														'&.Mui-checked': {
															color: '#7ca6bd',
														},
													}}
												/>
												<div style={{ flex: 1 }}>
													<div style={{ 
														fontWeight: '600', 
														marginBottom: '4px', 
														color: '#282828',
														fontSize: '1rem'
													}}>
														{task.category }
													</div>
													{task.task && (
														<div style={{ 
															fontSize: '0.875rem', 
															color: '#999',
															marginBottom: '8px',
															lineHeight: '1.4'
														}}>
															{task.task}
														</div>
													)}
													<div style={{ 
														display: 'flex', 
														gap: '16px', 
														fontSize: '0.8rem', 
														color: '#7ca6bd',
														alignItems: 'center'
													}}>
														{task.category && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
																<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
																	<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
																	<line x1="7" y1="7" x2="7.01" y2="7"></line>
																</svg>
																planning
															</span>
														)}
														{task.minMonths !== undefined && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
																<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
																	<circle cx="12" cy="12" r="10"></circle>
																	<polyline points="12 6 12 12 16 14"></polyline>
																</svg>
																{task.minMonths} {task.minMonths === 1 ? 'month' : 'months'}
															</span>
														)}
													</div>
												</div>
											</div>
										)
									})}
								</div>
							) : (
								<p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
									No tasks available for this wedding type.
								</p>
							)}
						</div>
					)}
				</div>
				<div className={`${styles.buttonContainer} ${styles.buttonContainerCenter}`}>
					<Stepper activeStep={1.5} />
					<ButtonCustom
						size="lg"
						onClick={handleContinue}
						disabled={loading}
					>
						Next
					</ButtonCustom>
				</div>
			</div>
		</NewWedingModal>
	)
}

export default TaskListModal
